import type {
  ReturnRequestDto,
  ReturnRequestLineItemDto,
} from "@babackapp/private-api-client/types";
import { getQuantityHandledMerchant } from "@/utils/return-requests";

export enum RefundModalViewModelType {
  NoReturnRequestFound = "NoReturnRequestFound",
  HasAdminRigths = "HasAdminRigths",
  HasNoAdminRigths = "HasNoAdminRigths",
}

export function getMaxQuantityValidated(lineItem: ReturnRequestLineItemDto) {
  return (
    getQuantityHandledLogistician(lineItem, {
      status: "ok",
    }) -
    getQuantityHandledMerchant(lineItem, {
      status: "ok",
    })
  );
}

export function getMaxQuantityRejected(lineItem: ReturnRequestLineItemDto) {
  return (
    getQuantityHandledLogistician(lineItem, {
      status: "rejected",
    }) -
    Math.max(
      getQuantityHandledMerchant(lineItem, {
        status: "ok",
      }) -
        getQuantityHandledLogistician(lineItem, {
          status: "ok",
        }),
      0,
    )
  );
}

export function getQuantityToHandleLogistician(
  lineItem: ReturnRequestLineItemDto,
): number {
  const alreadyHandledByLog = lineItem.logistician_informations?.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );

  const alreadyHandledByMerchant = lineItem.merchant_informations?.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );
  return (
    lineItem.quantity - Math.max(alreadyHandledByLog, alreadyHandledByMerchant)
  );
}

function getDiscountBonusAmountFromAmount(
  returnRequest: ReturnRequestDto,
  initialAmount: number,
) {
  const bonus = returnRequest.financial_informations.discount_bonus.bonus;
  if (bonus.type === "fixed_amount" && initialAmount > 0) {
    if (returnRequest.financial_informations.discount_bonus.done) return 0;
    return bonus.amount;
  } else if (bonus.type === "percentage") {
    const amount = initialAmount * (bonus.amount / 100);
    return amount;
  }
  return 0;
}

function _getAmountToDiscountWithoutBonus(
  returnRequest: ReturnRequestDto,
  opt?: { notValidatedByLogistician: boolean },
) {
  let amount = 0;
  const lineItemsToDiscount = returnRequest.line_items.filter(
    (li) => li.resolution === "discount",
  );
  amount = lineItemsToDiscount.reduce(
    (prev: number, li) =>
      prev + getQuantityToHandleMerchant(li, opt) * getCustomerPaidPrice(li),
    0,
  );
  return amount;
}

export function getTotalDiscountToHandle(
  returnRequest: ReturnRequestDto,
  opt: {
    withBonus?: boolean;
    withCharges?: boolean;
    notValidatedByLogistician: boolean;
  },
) {
  let amount = _getAmountToDiscountWithoutBonus(returnRequest, {
    notValidatedByLogistician: opt.notValidatedByLogistician,
  });

  if (amount === 0) {
    return 0;
  }

  if (opt?.withBonus) {
    amount += getDiscountBonusAmountFromAmount(returnRequest, amount);
  }
  if (opt?.withCharges) {
    amount -= _getFinancialChargesAmount(returnRequest, "discount", false);
  }

  return Math.round(amount * 100) / 100;
}

function getCustomerPaidPrice(lineItem: ReturnRequestLineItemDto) {
  let price = parseFloat(lineItem.price);
  lineItem.unitTaxes.forEach((tax) => {
    if (!tax.includedInPrice) {
      price += tax.amount;
    }
  });
  lineItem.discountsAllocated.forEach((discount) => {
    if (discount.allocationMethod === "EACH") {
      price -= discount.amount / lineItem.quantity_bought;
    } else if (discount.allocationMethod === "ACROSS") {
      price -= discount.amount / lineItem.quantity_bought;
    }
  });
  price = Math.round((price + Number.EPSILON) * 100) / 100;
  return price;
}

function _getFinancialChargesAmount(
  returnRequest: ReturnRequestDto,
  type: "discount" | "refund",
  isDone: boolean,
) {
  let charges = 0;
  const fi = returnRequest.financial_informations;
  if (
    fi.return_shipping_paid?.method == type &&
    fi.return_shipping_paid.presentment_amount > 0 &&
    fi.return_shipping_paid.done == isDone
  ) {
    charges += fi.return_shipping_paid.presentment_amount;
  }
  if (
    fi.initial_shipping_refunded?.method == type &&
    fi.initial_shipping_refunded.presentment_amount > 0 &&
    fi.initial_shipping_refunded.done == isDone
  ) {
    charges -= Math.min(
      fi.initial_shipping_refunded.presentment_amount,
      fi.initial_shipping_refunded.fixedAmount < 0
        ? Number.POSITIVE_INFINITY
        : fi.initial_shipping_refunded.fixedAmount,
    );
  }

  return charges;
}

function getQuantityToHandleMerchant(
  lineItem: ReturnRequestLineItemDto,
  opt?: { notValidatedByLogistician: boolean },
): number {
  const baseQuantity =
    opt && opt.notValidatedByLogistician
      ? lineItem.quantity
      : getQuantityHandledLogistician(lineItem, { status: "ok" });
  const alreadyHandled = lineItem.merchant_informations.reduce(
    (previousValue, currentValue) =>
      previousValue + currentValue.quantity.valueOf(),
    0,
  );

  return Math.max(baseQuantity - alreadyHandled, 0);
}

function getQuantityHandledLogistician(
  lineItem: ReturnRequestLineItemDto,
  opt?: { status: "ok" | "rejected" },
): number {
  return lineItem.logistician_informations?.reduce(
    (previousValue, currentValue) => {
      if (opt) {
        if (opt.status == currentValue.status) {
          return previousValue + currentValue.quantity.valueOf();
        }
      }
      return previousValue;
    },
    0,
  );
}
